import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import EcoIcon from "../../../components/eco-icon/EcoIcon";
import EcoPageHeader from "../../../components/eco-page-header/EcoPageHeader";
import type { RootState } from "../../../store/store";
import { LOGINPAGE } from "../../auth/routes";
import { ESTOQUE_PAGE, ESTOQUE_BALANCO_DETALHES_PAGE } from "../routes";
import { SUPERVISOR_HOME_PAGE } from "../../supervisor/routes";
import { isAdminFromToken } from "../../../utils/authToken";
import { getMassBalanceStoresApi, StoreShort } from "../api/massBalanceApi";

import { useGoBack } from "../../../hooks/useGoBack";

export default function MassBalanceStoresPage() {
  const navigate = useNavigate();
  const token = useSelector((state: RootState) => state.auth.accessToken);
  const isAdmin = isAdminFromToken(token);
  const goBack = useGoBack(isAdmin ? ESTOQUE_PAGE() : SUPERVISOR_HOME_PAGE());

  const [stores, setStores] = useState<StoreShort[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");

  const visibleStores = useMemo(() => {
    const q = search.trim().toLowerCase();
    return q ? stores.filter((s) => s.nomeFantasia.toLowerCase().includes(q)) : stores;
  }, [stores, search]);

  useEffect(() => {
    if (!token) {
      toast.error("Você precisa estar logado.");
      navigate(LOGINPAGE(), { replace: true });
    }
  }, [token, navigate]);

  useEffect(() => {
    const loadStores = async () => {
      if (!token) return;
      try {
        setLoading(true);
        const data = await getMassBalanceStoresApi(token);
        setStores(data || []);
      } catch (err: any) {
        toast.error(err.message || "Erro ao carregar lojas.");
      } finally {
        setLoading(false);
      }
    };
    loadStores();
  }, [token]);

  if (!token) return null;

  return (
    <div className="estoqueLayout">
      <main className="estoqueMain">
        <div className="estoqueContainer">
          <EcoPageHeader
            crumbs={[...(isAdmin ? [{ label: "Estoque", onClick: () => navigate(ESTOQUE_PAGE()) }] : [{ label: "Estoque" }]), { label: "Balanço de massa" }]}
            title="Balanço de massa"
            subtitle="Escolha a loja para abrir ou continuar o balanço de hoje."
            onBack={goBack}
            backLabel="Voltar"
          />

          {stores.length > 6 ? (
            <div className="ecoSearch estoqueStoreSearch">
              <EcoIcon name="search" size={18} className="ecoSearch__icon" />
              <input
                type="search"
                className="ecoInput"
                aria-label="Buscar loja"
                placeholder="Buscar loja"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          ) : null}

          {loading ? (
            <p className="ecoEmpty">Carregando lojas...</p>
          ) : visibleStores.length === 0 ? (
            <p className="ecoEmpty">
              {stores.length === 0 ? "Nenhuma loja encontrada para balanço." : "Nenhuma loja com esse nome."}
            </p>
          ) : (
            <div className="estoqueStoreGrid">
              {visibleStores.map((store) => (
                <button
                  key={store.id}
                  type="button"
                  className="estoqueStoreCard"
                  onClick={() => navigate(ESTOQUE_BALANCO_DETALHES_PAGE(store.id))}
                >
                  <span className="estoqueStoreCard__top">
                    <span className="estoqueStoreCard__name">{store.nomeFantasia}</span>
                    <EcoIcon name="chevronRight" size={18} className="estoqueStoreCard__chevron" />
                  </span>
                  <span className="estoqueStoreCard__address">
                    {store.endereco || "Endereço não informado"}
                    {store.estado ? ` · ${store.estado}` : ""}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
